import { useCallback, useState } from "react";

function getValueFromSessionStorage<T>(
  key: string,
  storage: Storage,
): T | null {
  if (typeof storage === "undefined") {
    return null;
  }
  const storedValue = storage.getItem(key);
  try {
    return storedValue == null ? null : (JSON.parse(storedValue) as T);
  } catch (error) {
    console.error(error);
  }

  return storedValue as unknown as T | null;
}

function saveValueToSessionStorage<T>(
  key: string,
  value: T,
  storage: Storage,
): void {
  if (typeof storage === "undefined") {
    return;
  }

  storage.setItem(key, JSON.stringify(value));
}

function initialize<S>(
  key: string,
  initialState: S | undefined,
  storage: Storage,
): S | undefined {
  const valueLoadedFromSessionStorage = getValueFromSessionStorage<S>(
    key,
    storage,
  );
  if (valueLoadedFromSessionStorage === null) {
    return initialState;
  } else {
    return valueLoadedFromSessionStorage;
  }
}

export function usePersistedStorageState<S>(
  key: string,
  initialState?: S | (() => S),
  storage = sessionStorage,
): [S | undefined, (v: S) => void, () => void] {
  const [value, __setValue] = useState<S | undefined>(() =>
    initialize(
      key,
      initialState instanceof Function ? initialState() : initialState,
      storage,
    ),
  );

  const setValue = useCallback(
    (newValue: S) => {
      __setValue(newValue);
      saveValueToSessionStorage(key, newValue, storage);
    },
    [key, storage],
  );

  const remove = useCallback(() => {
    storage.removeItem(key);
  }, [key, storage]);

  return [value, setValue, remove];
}
